/**
 * Idle tracking, so that "signed out for inactivity" means what it says.
 *
 * The server's idle window is the refresh token's lifetime, but that alone
 * does not bound a session: this app polls (the dashboard refetches background
 * jobs and notifications on a timer), and every poll that hits a 401 rotates
 * the refresh pair. A tab left open on a locked, unattended PC therefore
 * renews itself indefinitely with nobody at the keyboard — which is precisely
 * how DPO users stayed signed in across a passphrase change.
 *
 * So the client refuses to spend a rotation when no human has touched the app
 * inside the window. Last-activity lives in localStorage, which makes it
 * shared across tabs of the same browser — any one active tab keeps the
 * session alive, and all of them go idle together.
 *
 * This is a convenience bound, not the security boundary: it runs in the
 * user's own browser and could be tampered with. The bounds that actually
 * bind are server-side (refresh-token expiry and the absolute cap in
 * apps.accounts.session_auth). Someone editing their own localStorage to stay
 * signed in slightly longer gains nothing they could not get by wiggling the
 * mouse.
 */

const LAST_ACTIVITY_KEY = "hriswb.lastActivity";

/** Keep in step with the backend's JWT_REFRESH_TTL_HOURS (8h). Overridable so
 *  a kiosk-ish deployment can be configured without a code change. */
const IDLE_LIMIT_MINUTES = Number(
  import.meta.env.VITE_IDLE_TIMEOUT_MINUTES ?? 480,
);

const IDLE_LIMIT_MS = IDLE_LIMIT_MINUTES * 60_000;

/** localStorage writes are cheap but not free, and these events fire on every
 *  keystroke. One write per 30s is ample resolution for an 8h window. */
const WRITE_THROTTLE_MS = 30_000;

const ACTIVITY_EVENTS = [
  "pointerdown",
  "keydown",
  "wheel",
  "touchstart",
] as const;

let lastWrite = 0;

function write(now: number): void {
  try {
    localStorage.setItem(LAST_ACTIVITY_KEY, String(now));
    lastWrite = now;
  } catch {
    // Private mode / blocked storage. Without a stored timestamp
    // `isIdle` reports false, so the session simply falls back to the
    // server-side bounds. Degrading open is right: locking someone out
    // because their browser won't persist a number would be worse.
  }
}

/** Stamp "a human was here". Throttled; call it as often as you like. */
export function recordActivity(): void {
  const now = Date.now();
  if (now - lastWrite < WRITE_THROTTLE_MS) return;
  write(now);
}

/** Stamp unconditionally — used at login, where the throttle must not
 *  swallow the write that seeds a brand-new session. */
export function resetActivity(): void {
  write(Date.now());
}

export function clearActivity(): void {
  try {
    localStorage.removeItem(LAST_ACTIVITY_KEY);
  } catch {
    /* see write() */
  }
}

/** True when no interaction has been recorded inside the idle window.
 *
 *  An absent timestamp counts as *not* idle: a session that predates this
 *  code (or a browser that can't store it) gets seeded here rather than
 *  being signed out on the spot. */
export function isIdle(): boolean {
  let raw: string | null = null;
  try {
    raw = localStorage.getItem(LAST_ACTIVITY_KEY);
  } catch {
    return false;
  }
  if (raw === null) {
    resetActivity();
    return false;
  }
  const last = Number(raw);
  if (!Number.isFinite(last)) {
    resetActivity();
    return false;
  }
  return Date.now() - last >= IDLE_LIMIT_MS;
}

export function idleLimitMinutes(): number {
  return IDLE_LIMIT_MINUTES;
}

/** Attach the listeners. Returns a detach function (tests, HMR). */
export function startActivityTracking(): () => void {
  if (typeof window === "undefined") return () => {};

  const onActivity = () => recordActivity();
  // Coming back to a backgrounded tab is a deliberate act, so it counts;
  // leaving one open and walking away does not.
  const onVisibility = () => {
    if (document.visibilityState === "visible") recordActivity();
  };

  for (const event of ACTIVITY_EVENTS) {
    window.addEventListener(event, onActivity, { passive: true });
  }
  document.addEventListener("visibilitychange", onVisibility);

  return () => {
    for (const event of ACTIVITY_EVENTS) {
      window.removeEventListener(event, onActivity);
    }
    document.removeEventListener("visibilitychange", onVisibility);
  };
}
